import {
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { OrganizationMember, UserPlanType } from 'features/users/users.types';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link, matchPath } from 'react-router-dom';
import { useOrganizationMembers } from 'features/users/hooks/useOrganizationMembers';
import { useAuthState } from 'state/useAuthState';
import { SIMPLE_PAGE_LAYOUT_ID } from 'components/layouts/SimplePageLayout';
import {
  ComputeItemKey,
  FixedHeaderContent,
  ItemContent,
  ScrollerProps,
  TableComponents,
  TableVirtuoso,
  TableVirtuosoHandle,
} from 'react-virtuoso';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useScrollPositionState } from 'state/useScrollPositionState';
import { useDebounceValue } from 'usehooks-ts';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import Cancel from '@mui/icons-material/Cancel';
import { useQueryParams } from 'hooks/useQueryParams';
import { EMPTY_DATA_VALUE } from '../../../../constants';

const planTypeToLabelTransKey: Record<UserPlanType, string> = {
  regular: 'organization.member',
  pro: 'organization.proMember',
};

const LoadingSkeleton = (): JSX.Element[] => {
  return Array.from({ length: 5 }).map((_, idx) => (
    <Skeleton
      // eslint-disable-next-line @eslint-react/no-array-index-key
      key={idx}
      variant="rounded"
      sx={{ marginBottom: 3, marginX: 2 }}
      role="progressbar"
    />
  ));
};

const computeItemKey: ComputeItemKey<OrganizationMember, unknown> = (
  _: number,
  member: OrganizationMember,
) => member.id;

const tableComponents: TableComponents<OrganizationMember, unknown> = {
  Scroller: forwardRef<HTMLDivElement, ScrollerProps>((props, ref) => (
    <TableContainer
      {...props}
      ref={ref}
      style={{ ...(props.style || {}), overflow: 'hidden' }}
    />
  )),
  Table: (props) => (
    <Table
      {...props}
      size="small"
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead: forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow,
  TableBody: forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

export const OrganizationMembers = (): JSX.Element | null => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const [params, setParams] = useQueryParams<{ search: string | null }>();
  const searchInput = params.search ?? '';
  const [debouncedSearch] = useDebounceValue(searchInput, 350);
  const {
    fetchNextPage,
    hasNextPage,
    members,
    isLoading,
    totalCount,
    isFetchingNextPage,
  } = useOrganizationMembers({
    enabled: canManageOrganization,
    query: debouncedSearch,
  });
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);
  const { scrollPosition, setScrollPosition, reset } = useScrollPositionState(
    'organization-members',
  );

  useEffect(() => {
    return () => {
      // Leaving page
      const nextPath = window.location.pathname;

      const match = matchPath<{ memberId?: string }>(nextPath, {
        path: '/organization/members/:memberId',
        exact: true,
      });

      const memberId = Number.parseInt(match?.params?.memberId ?? '', 10);

      if (Number.isNaN(memberId)) {
        // Reset scroll position
        reset();
      }
    };
  }, [reset]);

  const customScrollParent = document.getElementById(
    SIMPLE_PAGE_LAYOUT_ID,
  ) as HTMLElement;

  const fixedHeaderContent: FixedHeaderContent = useCallback(
    () => (
      <TableRow>
        <TableCell colSpan={2}>
          <Typography
            variant="h4"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightBold,
              fontSize: theme.typography.body2.fontSize,
            })}
          >
            {t('organization.name')}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="h4"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightBold,
              fontSize: theme.typography.body2.fontSize,
            })}
          >
            {t('organization.status')}
          </Typography>
        </TableCell>
      </TableRow>
    ),
    [t],
  );

  const handleEndReached = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const renderItemContent: ItemContent<OrganizationMember, unknown> =
    useCallback(
      (_, member) => {
        return (
          <Fragment key={member.id}>
            <TableCell colSpan={2}>
              <Stack
                component={Link}
                to={`/organization/members/${member.id}`}
                sx={{ textDecoration: 'none', color: 'inherit' }}
                onClick={() =>
                  virtuosoRef.current?.getState(({ scrollTop }) =>
                    setScrollPosition(scrollTop),
                  )
                }
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.firstName} {member.lastName}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="secondary"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.email}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                component={Link}
                to={`/organization/members/${member.id}`}
                sx={{ textDecoration: 'none', color: 'inherit' }}
                onClick={() =>
                  virtuosoRef.current?.getState(({ scrollTop }) =>
                    setScrollPosition(scrollTop),
                  )
                }
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {member.planType
                    ? t(planTypeToLabelTransKey[member.planType])
                    : EMPTY_DATA_VALUE}
                </Typography>
                <KeyboardArrowRightIcon />
              </Stack>
            </TableCell>
          </Fragment>
        );
      },
      [t, setScrollPosition],
    );

  // Ensures consistent virtuoso table rendering by setting minimum height
  const style = useMemo(() => ({ minHeight: members.length * 45 }), [members]);

  return (
    <>
      <Stack spacing={1} sx={{ px: 2, py: 1 }}>
        <Typography variant="h3" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('organization.people')}
        </Typography>

        <TextField
          placeholder={t('organization.search')}
          variant="outlined"
          value={searchInput}
          onChange={(e) => setParams({ search: e.target.value })}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setParams({ search: null })}
                    sx={{ marginRight: -1 }}
                    aria-label={t('organization.clearSearch')}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      <TableVirtuoso
        ref={virtuosoRef}
        data={members}
        totalCount={totalCount}
        endReached={handleEndReached}
        customScrollParent={customScrollParent}
        itemContent={renderItemContent}
        computeItemKey={computeItemKey}
        components={tableComponents}
        fixedHeaderContent={fixedHeaderContent}
        style={style}
        initialScrollTop={scrollPosition}
      />

      {isLoading && <LoadingSkeleton />}

      {Boolean(searchInput) && !isLoading && members.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" align="center" color="textSecondary">
            {t('organization.noMembersFound')}
          </Typography>
        </Box>
      )}
    </>
  );
};
